import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { BookOpen, Plus, Trash2, Save, X, Loader2, ShieldAlert, Pencil, CheckCircle2, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '@/lib/permissions';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

// Admin-only curated question→SQL example library for a SQL-warehouse model.
// These examples are retrieved by semantic similarity and injected into the
// AI Explore text-to-SQL prompt to make hard questions reliable.
export const SqlExamplesPanel = ({ modelId, onClose }) => {
  const { can } = usePermissions();
  const canWrite = can('ai_copilot:sql_write');
  const canDelete = can('ai_copilot:sql_delete');
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // example_id being edited, or 'new'
  const [q, setQ] = useState('');
  const [sql, setSql] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // example pending delete confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listSqlExamples(modelId);
      setExamples(res.examples || []);
    } catch (e) {
      toast.error(e?.message || 'Could not load examples');
    } finally { setLoading(false); }
  }, [modelId]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing('new'); setQ(''); setSql(''); setError(null); };
  const startEdit = (ex) => { setEditing(ex.example_id); setQ(ex.question); setSql(ex.sql); setError(null); };
  const cancel = () => { setEditing(null); setQ(''); setSql(''); setError(null); };

  const save = async () => {
    if (!q.trim() || !sql.trim()) { setError('Question and SQL are both required.'); return; }
    setSaving(true); setError(null);
    try {
      if (editing === 'new') {
        await api.createSqlExample({ model_id: modelId, question: q.trim(), sql: sql.trim() });
        toast.success('Example added');
      } else {
        await api.updateSqlExample(editing, { question: q.trim(), sql: sql.trim() });
        toast.success('Example updated');
      }
      cancel();
      load();
    } catch (e) {
      setError(e?.message || 'Could not save example.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.deleteSqlExample(id);
      setExamples(prev => prev.filter(e => e.example_id !== id));
      toast.success('Example deleted');
    } catch (e) {
      toast.error(e?.message || 'Could not delete example');
    }
  };

  const approve = async (id) => {
    try {
      await api.approveSqlExample(id);
      toast.success('Approved — AI Explore will reuse this for similar questions');
      load();
    } catch (e) {
      toast.error(e?.message || 'Could not approve suggestion');
    }
  };

  const pending = examples.filter(e => e.status === 'suggested');
  const approved = examples
    .filter(e => e.status !== 'suggested')
    .sort((a, b) => (b.reuse_count || 0) - (a.reuse_count || 0));
  const totalReuses = examples.reduce((s, e) => s + (e.reuse_count || 0), 0);

  return (
    <div className="w-full rounded-2xl border border-sky-500/30 bg-card shadow-2xl p-4 mb-6" data-testid="sql-examples-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-medium text-sky-300">
          <BookOpen className="w-4 h-4" /> SQL Library
          <span className="text-[10px] font-normal text-muted-foreground border border-border/40 rounded-full px-2 py-0.5">grounds the AI · admin</span>
        </div>
        <div className="flex items-center gap-2">
          {!editing && canWrite && (
            <Button size="sm" variant="outline" onClick={startNew} data-testid="sql-example-new-btn">
              <Plus className="w-3.5 h-3.5" /><span className="ml-1.5">Add example</span>
            </Button>
          )}
          {onClose && <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="sql-examples-close"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3">
        Curated question → SQL pairs teach the AI how to answer tricky questions for this model. The most relevant examples are automatically added to the AI's prompt when a similar question is asked.
      </p>

      {!loading && totalReuses > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2" data-testid="correction-insights">
          <Repeat className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-[11px] text-emerald-800 dark:text-emerald-200">
            Your taught fixes have been reused <b>{totalReuses}×</b> to answer questions{approved[0]?.reuse_count ? <> — top: <b className="font-medium">“{approved[0].question}”</b> ({approved[0].reuse_count}×)</> : null}
          </span>
        </div>
      )}

      {editing && (
        <div className="rounded-xl border border-border/40 bg-background/50 p-3 mb-4" data-testid="sql-example-editor">
          <label className="block text-[11px] text-muted-foreground mb-1">Question (plain English)</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. loss ratio by year for new vs renewals"
            className="w-full bg-background/60 border border-border/40 rounded-lg px-3 py-2 text-xs text-foreground outline-none focus:border-primary/40 mb-3"
            data-testid="sql-example-question-input"
          />
          <label className="block text-[11px] text-muted-foreground mb-1">SQL (read-only SELECT / WITH)</label>
          <textarea
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            rows={7}
            spellCheck={false}
            placeholder="WITH ... SELECT ... — a single read-only statement. SELECT * and PII columns are blocked."
            className="w-full font-mono text-xs bg-background/70 border border-border/40 rounded-lg px-3 py-2 text-foreground outline-none focus:border-primary/40 resize-y"
            data-testid="sql-example-sql-input"
          />
          {error && (
            <div className="mt-2 flex items-start gap-2 text-[11px] text-red-400" data-testid="sql-example-error">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={save} disabled={saving} data-testid="sql-example-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              <span className="ml-1.5">{editing === 'new' ? 'Add' : 'Save'}</span>
            </Button>
            <Button size="sm" variant="ghost" onClick={cancel} data-testid="sql-example-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      {!loading && pending.length > 0 && (
        <div className="mb-4" data-testid="sql-examples-pending">
          <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-700 dark:text-amber-300 mb-2">
            <ShieldAlert className="w-3.5 h-3.5" /> Suggested by users — awaiting your approval ({pending.length})
          </div>
          <div className="space-y-2">
            {pending.map((ex) => (
              <div key={ex.example_id} className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid={`sql-suggestion-${ex.example_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-foreground truncate">{ex.question}</div>
                    <pre className="mt-1.5 font-mono text-[10.5px] text-sky-300/80 bg-[#0d1117] p-2 rounded-lg overflow-auto max-h-32 whitespace-pre-wrap leading-relaxed">{ex.sql}</pre>
                    <span className="mt-1 inline-block text-[9px] text-muted-foreground">suggested via a thumbs-down fix{ex.created_by_email ? ` · ${ex.created_by_email}` : ''}</span>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => approve(ex.example_id)} className="text-emerald-600 hover:text-emerald-500" title="Approve" data-testid={`sql-suggestion-approve-${ex.example_id}`}><CheckCircle2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(ex.example_id)} className="text-muted-foreground hover:text-red-400" title="Reject" data-testid={`sql-suggestion-reject-${ex.example_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-4 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading examples…</div>
      ) : approved.length === 0 && !editing ? (
        <div className="p-4 text-xs text-muted-foreground" data-testid="sql-examples-empty">
          No saved queries yet. Add one above, or give any good SQL answer a 👍 thumbs-up to save it here.
        </div>
      ) : (
        <div className="space-y-2" data-testid="sql-examples-list">
          {approved.map((ex) => (
            <div key={ex.example_id} className="rounded-xl border border-border/30 bg-background/40 p-3" data-testid={`sql-example-${ex.example_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-foreground truncate">{ex.question}</div>
                  <pre className="mt-1.5 font-mono text-[10.5px] text-sky-300/80 bg-[#0d1117] p-2 rounded-lg overflow-auto max-h-32 whitespace-pre-wrap leading-relaxed">{ex.sql}</pre>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {ex.source === 'saved' && <span className="text-[9px] text-muted-foreground">captured from an answer</span>}
                    {(ex.reuse_count || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5" data-testid={`sql-example-reuse-${ex.example_id}`} title={ex.last_reused_at ? `Last reused ${new Date(ex.last_reused_at).toLocaleString()}` : ''}>
                        <Repeat className="w-2.5 h-2.5" /> reused {ex.reuse_count}×
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {canWrite && <button onClick={() => startEdit(ex)} className="text-muted-foreground hover:text-foreground" title="Edit" data-testid={`sql-example-edit-${ex.example_id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                  {canDelete && <button onClick={() => setConfirmDelete(ex)} className="text-muted-foreground hover:text-red-400" title="Delete" data-testid={`sql-example-delete-${ex.example_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="sql-example-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this saved query?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.question ? `"${confirmDelete.question}"` : 'This query'} will be permanently removed from the SQL Library and the AI will no longer use it to ground similar questions. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sql-example-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="sql-example-delete-confirm-btn"
              onClick={() => { const id = confirmDelete?.example_id; setConfirmDelete(null); if (id) remove(id); }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
